import { z } from "zod";

import { MetadataSchema } from "api/schemas/common";

import {
  DurationIconSchema,
  DurationUnitSchema,
  EquipmentSchema,
  LocaleSchema,
  MovementSchema,
  PositionSchema,
  PrimaryFunctionSchema,
  StateSchema,
  TargetJointsSchema,
  VisibilitySchema,
} from "./shared";

export const LevelSchema = z.object({
  about: z.string().nullable(),
  caption_file_url: z.string().nullable().optional(),
  created_at: z.string(),
  deprecated_at: z.string().nullable(),
  duration_icon: DurationIconSchema.nullable().optional(),
  duration_unit: DurationUnitSchema.nullable().optional(),
  equipment: z.array(EquipmentSchema).nullable().optional(),
  exercise_id: z.number(),
  id: z.number(),
  image_url: z.string().nullable(),
  instructions_icon_name: DurationIconSchema.nullable().optional(),
  instructions_text1: z.string().nullable().optional(),
  instructions_text2: z.string().nullable().optional(),
  instructions_text3: z.string().nullable().optional(),
  level: z.number(),
  movement: MovementSchema.nullable().optional(),
  position: PositionSchema.nullable().optional(),
  reps: z.string().nullable(),
  sets: z.number().nullable(),
  tip_description: z.string().nullable(),
  tip_title: z.string().nullable(),
  title: z.string(),
  updated_at: z.string(),
  variation_title: z.string().nullable(),
  video_file_url: z.string().nullable(),
  video_thumbnail_file_url: z.string().nullable(),
});

const LevelItemSchema = z.object({
  ...LevelSchema.shape,
  extra_swedish_description: z.string().nullable().optional(),
  translation_id: z.number(),
  video_url: z.string().nullable().optional(),
});
export type LevelItem = z.infer<typeof LevelItemSchema>;

const ExerciseSchema = z.object({
  created_at: z.string().nullable(),
  creator_id: z.number().nullable(),
  duration_minutes: z.number().nullable(),
  exercise_number: z.number(),
  levels: z.array(LevelSchema),
  name: z.string(),
  premium: z.boolean(),
  primary_function: PrimaryFunctionSchema,
  state: StateSchema,
  target_joints: z.array(TargetJointsSchema),
  updated_at: z.string(),
  visibility: VisibilitySchema,
});
export type Exercise = z.infer<typeof ExerciseSchema>;

const ExerciseListItemSchema = z.object({
  ...ExerciseSchema.shape,
  deleted_at: z.string().nullable(),
  exercise_id: z.number(),
  levels: z.array(LevelSchema),
  number_of_levels: z.number(),
});
export type ExerciseListItem = z.infer<typeof ExerciseListItemSchema>;

const ExerciseItemSchema = z.object({
  ...ExerciseSchema.shape,
  id: z.number(),
  levels: z.array(LevelItemSchema),
});
export type ExerciseItem = z.infer<typeof ExerciseItemSchema>;

export const GetExercisesResponseSchema = z.object({
  items: z.array(ExerciseListItemSchema),
  metadata: MetadataSchema,
});
export type GetExercisesResponse = z.infer<typeof GetExercisesResponseSchema>;

const PostLevelPayloadSchema = z.object({
  duration_icon: DurationIconSchema.nullable().optional(),
  duration_unit: DurationUnitSchema,
  equipment: z.array(EquipmentSchema),
  level: z.number().nullable().optional(),
  movement: MovementSchema.nullable(),
  position: PositionSchema.nullable(),
  reps: z.string().nullable(),
  sets: z.number().nullable(),
});
export type PostLevelPayload = z.infer<typeof PostLevelPayloadSchema>;

export const PostLevelResponseSchema = z.object({
  ...PostLevelPayloadSchema.shape,
  created_at: z.string(),
  deprecated_at: z.string().nullable(),
  exercise_id: z.number(),
  id: z.number(),
  level: z.number(),
  updated_at: z.string(),
});

const PostTranslationPayloadSchema = z.object({
  about: z.string(),
  caption_file_url: z.string().nullable().optional(),
  image_url: z.string(),
  instructions_icon_name: z.enum(["repetitions", "timer"]).nullable().optional(),
  instructions_text1: z.string().nullable().optional(),
  instructions_text2: z.string().nullable().optional(),
  instructions_text3: z.string().nullable().optional(),
  locale: LocaleSchema,
  tip_description: z.string(),
  tip_title: z.string(),
  title: z.string(),
  variation_title: z.string().nullable().optional(),
  video_file_url: z.string().nullable().optional(),
  video_thumbnail_file_url: z.string().nullable().optional(),
  video_url: z.string().nullable().optional(),
});
export type PostTranslationPayload = z.infer<typeof PostTranslationPayloadSchema>;

export const PostTranslationResponseSchema = z.object({
  about: z.string(),
  caption_file_url: z.string().nullable(),
  created_at: z.string(),
  exercise_level_id: z.number(),
  extra_swedish_description: z.string().nullable(),
  id: z.number(),
  image_url: z.string().nullable(),
  instructions_icon_name: DurationIconSchema,
  instructions_text1: z.string().nullable(),
  instructions_text2: z.string().nullable(),
  instructions_text3: z.string().nullable(),
  locale: LocaleSchema,
  tip_description: z.string(),
  tip_title: z.string(),
  title: z.string(),
  updated_at: z.string(),
  variation_title: z.string().nullable(),
  video_file_url: z.string().nullable(),
  video_thumbnail_file_url: z.string().nullable(),
  video_url: z.string().nullable(),
});

export const PostExercisePayloadSchema = z.object({
  levels: z.array(
    z.object({
      ...PostLevelPayloadSchema.shape,
      image_url: z.string(),
      translations: z.array(PostTranslationPayloadSchema.omit({ image_url: true })),
      video: z.object({
        video_file_url: z.string(),
        video_thumbnail_file_url: z.string().nullable().optional(),
      }),
    })
  ),
  primary_function: PrimaryFunctionSchema,
  target_joints: z.array(TargetJointsSchema),
});
export type PostExercisePayload = z.infer<typeof PostExercisePayloadSchema>;

export const PostExerciseResponseSchema = z.object({
  created_at: z.string(),
  creator_id: z.number(),
  duration_minutes: z.number().nullable(),
  exercise_number: z.number(),
  id: z.number(),
  name: z.string(),
  premium: z.boolean(),
  primary_function: PrimaryFunctionSchema,
  state: StateSchema,
  target_joints: z.array(TargetJointsSchema),
  updated_at: z.string(),
  visibility: VisibilitySchema,
});
export type PostExerciseResponse = z.infer<typeof PostExerciseResponseSchema>;

const PutExerciseArgsSchema = z.object({
  exerciseID: z.number(),
  data: PostExercisePayloadSchema,
});
export type PutExerciseArgs = z.infer<typeof PutExerciseArgsSchema>;

const PutExerciseLevelArgsSchema = z.object({
  exerciseID: z.number(),
  exerciseLevelID: z.number(),
  data: PostLevelPayloadSchema,
});
export type PutExerciseLevelArgs = z.infer<typeof PutExerciseLevelArgsSchema>;

const PutExerciseTranslationArgsSchema = z.object({
  exerciseID: z.number(),
  exerciseLevelID: z.number(),
  exerciseLevelTranslationID: z.number(),
  data: PostTranslationPayloadSchema,
});
export type PutExerciseTranslationArgs = z.infer<typeof PutExerciseTranslationArgsSchema>;

// FORM SCHEMAS AND TYPES

const ExerciseLevelTranslationFormDataSchema = z.object({
  about: z.string().min(1),
  caption_file_url: z.string().nullable().optional(),
  locale: LocaleSchema,
  tip_description: z.string(),
  tip_title: z.string(),
  title: z.string().min(1),
});
export type ExerciseLevelTranslationFormData = z.infer<typeof ExerciseLevelTranslationFormDataSchema>;

const ExerciseLevelFormDataSchema = z
  .object({
    duration_unit: DurationUnitSchema,
    equipment: z.array(EquipmentSchema),
    movement: MovementSchema.or(z.literal("")),
    position: PositionSchema.or(z.literal("")),
    image_url: z.string().or(z.literal("")),
    reps: z.string().trim().min(1),
    sets: z.number().min(1),
    translations: z.array(ExerciseLevelTranslationFormDataSchema),
    video: z.object({
      video_file_url: z.string().or(z.literal("")),
      video_thumbnail_file_url: z.string().nullable().optional(),
    }),
  })
  .refine(data => data.image_url !== "" || data.video.video_file_url !== "", {
    message: "required",
    path: ["image_url"],
  });
export type ExerciseLevelFormData = z.infer<typeof ExerciseLevelFormDataSchema>;

export const ExerciseFormDataSchema = z.object({
  levels: z.array(ExerciseLevelFormDataSchema),
  primary_function: PrimaryFunctionSchema,
  target_joints: z.array(TargetJointsSchema),
});
export type ExerciseFormData = z.infer<typeof ExerciseFormDataSchema>;

const EditExerciseFormDataSchema = z.object({
  ...ExerciseFormDataSchema.shape,
  exerciseID: z.number(),
  exerciseLevelID: z.number(),
  exerciseLevelTranslationID: z.number(),
});
export type EditExerciseFormData = z.infer<typeof EditExerciseFormDataSchema>;

// END OF FORM SCHEMAS AND TYPES
